import React, { useRef } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';

import ModalLoadingInfo from 'pctComponents/modals/ModalLoadingInfo';
import { OriginNavigation } from 'pctComponents/organisms/ManagerTrial/@model/ManagerTrial.model';
import { createNodeEnrollments, findContentStudents } from 'services/api';
import { getProfileInfo } from 'services/utils';

import useBackControll from './useBackControll';
import useCards from './useCards';
import usePlan from './usePlan';

type Props = {
    collection: BlockElement | any;
    direction?: string;
    unlockNext?: boolean;
    numberRequest?: number;
    userIsAdmin?: boolean;
    hasRequestedUpgrade?: boolean;
    isUpgradeButtonActive?: boolean;
    navigationTarget?: any;
    targetItem?: any;
    parentNodeEnrollment?: any;
    prevItem?: any;
};

const useContentNavigationBtn = ({
    collection,
    direction,
    unlockNext = false,
    numberRequest = 0,
    userIsAdmin = false,
    hasRequestedUpgrade = false,
    isUpgradeButtonActive = false,
    navigationTarget,
    targetItem,
    parentNodeEnrollment,
    prevItem,
}: Props) => {
    const userProfile = getProfileInfo();
    const { openModalUpgrade } = usePlan();
    const modalLoading = useModal('ModalLoadingInfo');
    const sequentialText = 'Conclua o conteúdo anterior para continuar.';
    NiceModal.register('ModalLoadingInfo', ModalLoadingInfo);
    const setProgressStateRef = useRef(null as any);
    const progressInfoRef = useRef(null as any);
    const { isfluidNavigationFn } = useBackControll({});

    const openModal = () => {
        if (!isUpgradeButtonActive) {
            return;
        }
        if (!userIsAdmin && hasRequestedUpgrade) {
            return;
        }
        openModalUpgrade({
            userIsAdmin,
            hasRequestedUpgrade,
            numberRequest,
            originNavigation: OriginNavigation.fluidNavigation,
        });
    };

    let currentContentCollection: any = navigationTarget || {
        data: {},
        elementType: '',
        contentEnrollment: {},
        idNodeEnrollment: 0,
        idEnrollment: 0,
        idElement: 0,
        idEvaluation: null,
        nodeCode: null,
    };

    if (
        !navigationTarget &&
        ((direction === 'next' && collection.nextItem != null) ||
            (direction === 'prev' && collection.previousItem != null))
    ) {
        currentContentCollection = {
            data: {
                contentEnrollment:
                    direction === 'next'
                        ? { idContentEnrollment: collection.nextItem.idContentEnrollment }
                        : {
                              idContentEnrollment: collection.previousItem.idContentEnrollment,
                          },
                idContentType:
                    direction === 'next' ? collection.nextItem.idContentType : collection.previousItem.idContentType,
                idEnrollment:
                    direction === 'next' ? collection.nextItem.idEnrollment : collection.previousItem.idEnrollment,
                idEvaluation:
                    direction === 'next' ? collection.nextItem.idEvaluation : collection.previousItem.idEvaluation,
                idNodeEnrollment:
                    direction === 'next'
                        ? collection.nextItem.idNodeEnrollment
                        : collection.previousItem.idNodeEnrollment,
                idQuestionnaire:
                    direction === 'next'
                        ? collection.nextItem.hasOwnProperty('idQuestionnaire')
                            ? collection.nextItem?.idQuestionnaire
                            : collection.nextItem.hasOwnProperty('questionnaire')
                            ? collection.nextItem?.questionnaire.idQuestionnaire
                            : null
                        : collection.previousItem.hasOwnProperty('idQuestionnaire')
                        ? collection.previousItem?.idQuestionnaire
                        : collection.previousItem.hasOwnProperty('questionnaire')
                        ? collection.previousItem?.questionnaire.idQuestionnaire
                        : null,
                sequential: direction === 'next' ? collection.nextItem.sequential : collection.previousItem.sequential,
                nodeCode: direction === 'next' ? collection.nextItem.nodeCode : collection.previousItem.nodeCode,
            },

            elementType:
                direction === 'next'
                    ? collection.nextItem.idEvaluation
                        ? 'EVALUATION'
                        : 'CONTENT'
                    : collection.previousItem.idEvaluation
                    ? 'EVALUATION'
                    : 'CONTENT',
            contentEnrollment:
                direction === 'next'
                    ? { idContentEnrollment: collection.nextItem.idContentEnrollment }
                    : {
                          idContentEnrollment: collection.previousItem.idContentEnrollment,
                      },
            idNodeEnrollment:
                direction === 'next' ? collection.nextItem.idNodeEnrollment : collection.previousItem.idNodeEnrollment,
            idEnrollment:
                direction === 'next' ? collection.nextItem.idEnrollment : collection.previousItem.idEnrollment,
            idElement: direction === 'next' ? collection.nextItem.idContentItem : collection.previousItem.idContentItem,
            idEvaluation:
                direction === 'next' ? collection.nextItem.idEvaluation : collection.previousItem.idEvaluation,
            nodeCode: direction === 'next' ? collection.nextItem.nodeCode : collection.previousItem.nodeCode,
        };
    }

    const { handleCardMouseEvent } = useCards({
        collection: currentContentCollection,
        id: 0,
        dataParent: currentContentCollection,
        setProgressStateRef,
        progressInfoRef,
    });

    const hasPreviousItemCompleted = () => {
        let prevHasCompleted = true;

        if (prevItem) {
            if (prevItem.elementType === 'CONTENT' && prevItem.contentEnrollment) {
                if (prevItem.contentEnrollment.status !== 'COMPLETED') {
                    prevHasCompleted = false;
                }
            } else if (prevItem.elementType === 'CONTENT' && !prevItem.contentEnrollment) {
                prevHasCompleted = false;
            } else if (prevItem.elementType === 'NODE' && prevItem.nodeEnrollment) {
                if (prevItem.nodeEnrollment.status !== 'COMPLETED') {
                    prevHasCompleted = false;
                }
            } else if (prevItem.elementType === 'NODE' && !prevItem.nodeEnrollment) {
                prevHasCompleted = false;
            } else if (prevItem.elementType === 'EVALUATION') {
                if (prevItem.questionnaire) {
                    if (!prevItem.questionnaire.finishDate) {
                        prevHasCompleted = false;
                    }
                } else {
                    prevHasCompleted = false;
                }
            }
        }

        return prevHasCompleted;
    };

    const hasNextSequential = () => {
        let rt = false;
        let isEvaluationAndFinished = collection.hasOwnProperty('evaluation') ? collection.finishDate != null : false;

        if (targetItem) {
            rt = targetItem.sequential?.toString() === '1' && !hasPreviousItemCompleted();

            if (isEvaluationAndFinished) {
                rt = false;
            }
        } else if (collection.nextItem) {
            const currentStatus = collection.contentEnrollment ? collection.contentEnrollment.status : '';

            if (collection.nextItem.sequential && currentStatus !== 'COMPLETED' && unlockNext === false) {
                rt = true;
            }

            if (isEvaluationAndFinished) {
                rt = false;
            }
        }

        return rt;
    };

    const isScormContent = () => {
        let rt = false;
        const hasObjScorm = collection.hasOwnProperty('objScorm');

        if (hasObjScorm) {
            rt = true;
        }

        return rt;
    };

    const isEvaluationContent = () => {
        let rt = false;
        const hasEvaluation = currentContentCollection.elementType === 'EVALUATION' || collection.hasOwnProperty('evaluation');

        if (hasEvaluation) {
            rt = true;
        }

        return rt;
    };

    const getNavigationNodeCode = () =>
        currentContentCollection.nodeCode ||
        currentContentCollection.data?.nodeCode ||
        targetItem?.nodeCode ||
        targetItem?.nodeEnrollment?.node?.nodeCode ||
        targetItem?.nodeEnrollment?.nodeCode ||
        parentNodeEnrollment?.node?.nodeCode ||
        parentNodeEnrollment?.nodeCode ||
        collection.currentItem?.nodeCode ||
        collection.nodeCode ||
        collection.rootContent?.nodeCode;

    const getExistingNodeEnrollmentId = () => {
        const navigationNodeCode = getNavigationNodeCode();
        const parentNodeCode = parentNodeEnrollment?.node?.nodeCode || parentNodeEnrollment?.nodeCode;

        return (
            currentContentCollection.idNodeEnrollment ||
            currentContentCollection.data?.idNodeEnrollment ||
            targetItem?.idNodeEnrollment ||
            targetItem?.contentEnrollment?.idNodeEnrollment ||
            targetItem?.questionnaire?.idNodeEnrollment ||
            (parentNodeEnrollment?.idNodeEnrollment && String(parentNodeCode) === String(navigationNodeCode)
                ? parentNodeEnrollment.idNodeEnrollment
                : null)
        );
    };

    const ensureCurrentNodeEnrollmentId = () => {
        const idNodeEnrollment = getExistingNodeEnrollmentId();

        if (idNodeEnrollment) {
            currentContentCollection.idNodeEnrollment = idNodeEnrollment;
            if (currentContentCollection.data) {
                currentContentCollection.data.idNodeEnrollment = idNodeEnrollment;
            }
        }

        return idNodeEnrollment;
    };

    const getNodeEnrollmentObj = () => ({
        idUser:
            targetItem?.idUser ||
            parentNodeEnrollment?.idUser ||
            collection.idUser ||
            collection.contentEnrollment?.idUser ||
            userProfile.idUser,
        idEnrollment:
            currentContentCollection.idEnrollment ||
            targetItem?.idEnrollment ||
            parentNodeEnrollment?.idEnrollment ||
            collection.contentEnrollment?.idEnrollment ||
            collection.rootContent?.idEnrollment,
        nodeCode: getNavigationNodeCode(),
        idTeam:
            targetItem?.idTeam ||
            targetItem?.contentEnrollment?.idTeam ||
            targetItem?.questionnaire?.idTeam ||
            parentNodeEnrollment?.idTeam ||
            collection.idTeam ||
            collection.rootContent?.idTeam ||
            collection.team?.[0]?.idTeam,
        idParentNodeEnrollment:
            parentNodeEnrollment?.idNodeEnrollment ||
            collection.rootContent?.idNodeEnrollment ||
            collection.idNodeEnrollment ||
            collection.contentEnrollment?.idNodeEnrollment,
    });

    const scormContentNextContent = (e) => {
        modalLoading.show({
            obj: {
                loadding: true,
                closeBtn: false,
                message: 'Aguarde, salvando dados do conteúdo!',
                className: 'navigationModal',
            },
        });

        collection.callLmsFinish(false);

        setTimeout(() => {
            findContentStudents(
                userProfile.idUser,
                collection.rootContent.idEnrollment,
                collection.rootContent.idContentItem,
                collection.rootContent.idNodeEnrollment,
            )
                .then(() => {
                    modalLoading.remove();
                    handleCardMouseEvent(e);
                })
                .catch((error) => {
                    console.log(error);
                });
        }, 2000);

        e.stopPropagation();
    };

    const createNodeEnrollmentForFluidNavigation = (nodeEnrollmentObj, _onFinishedEnrollment, e) => {
        modalLoading.show({
            obj: {
                loadding: true,
                closeBtn: false,
                message: 'Aguarde!',
                className: 'navigationModal',
            },
        });

        createNodeEnrollments(nodeEnrollmentObj)
            .then((result) => {
                modalLoading.remove();

                currentContentCollection.idNodeEnrollment = result.data.newId;
                if (currentContentCollection.data) {
                    currentContentCollection.data.idNodeEnrollment = result.data.newId;
                }
                _onFinishedEnrollment(e);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const handMouseNextPrevEvent = (e: React.MouseEvent<HTMLElement>) => {
        const currentNodeEnrollmentId = ensureCurrentNodeEnrollmentId();

        if (isScormContent()) {
            if (currentNodeEnrollmentId) {
                isfluidNavigationFn(true);
                scormContentNextContent(e);
            } else {
                const nodeEnrollmentObj = getNodeEnrollmentObj();

                createNodeEnrollmentForFluidNavigation(nodeEnrollmentObj, handMouseNextPrevEvent, e);
            }
        } else if (isEvaluationContent()) {
            
            if (currentNodeEnrollmentId) {
                isfluidNavigationFn(true);
                handleCardMouseEvent(e);
            } else {
                const nodeEnrollmentObj = getNodeEnrollmentObj();
                createNodeEnrollmentForFluidNavigation(nodeEnrollmentObj, handMouseNextPrevEvent, e);
            }
        } else if (!hasNextSequential()) {

            if (currentNodeEnrollmentId) {
                isfluidNavigationFn(true);
                handleCardMouseEvent(e);
            } else {
                const nodeEnrollmentObj = getNodeEnrollmentObj();
                createNodeEnrollmentForFluidNavigation(nodeEnrollmentObj, handMouseNextPrevEvent, e);
            }
        } else {
            modalLoading.show({
                obj: {
                    content: sequentialText,
                    closeBtn: true,
                    className: 'navigationModal',
                },
            });
        }

        openModal();
        e.stopPropagation();
    };

    return {
        handMouseNextPrevEvent,
        hasNextSequential,
    };
};

export default useContentNavigationBtn;
